import type { SheetCell } from "../../engine/cells";
import type { Id } from "../../engine/models";
import type { ApiLineItem } from "./types";

export const RATE_TYPE_OPTIONS = [
    { value: "Hours", label: "Hours", color: "primary" as const },
    { value: "Days", label: "Days", color: "success" as const },
    { value: "Weeks", label: "Weeks", color: "warning" as const },
    { value: "Flat", label: "Flat", color: "info" as const },
];

export const CURRENCY_OPTIONS = [
    { value: "USD", label: "USD" },
    { value: "CAD", label: "CAD" },
    { value: "EUR", label: "EUR" },
    { value: "GBP", label: "GBP" },
];

export function generateCellForLineItem(
    item: ApiLineItem,
    colId: string,
    parentId: Id | undefined,
    indentLevel: number,
): SheetCell {
    const isRunning = colId.startsWith("line_item_running.");
    const baseId = isRunning ? colId.replace("line_item_running.", "") : colId;

    switch (baseId) {
        case "_order":
            return {
                type: "statusDot",
                text: "☰",
                nonEditable: true,
                style: {
                    cursor: "grab",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            };
        case "custom_id":
            return { type: "header", text: "", nonEditable: true };
        case "name":
            return {
                type: "chevron",
                text: item.name,
                isExpanded: false,
                hasChildren: false,
                parentId,
                indentLevel,
                isDeletable: true,
                style: { display: "flex", alignItems: "center" },
            };
        case "is_internal":
            return {
                type: "dropdown",
                value: item.is_internal ? "Internal" : "External",
                options: [
                    { value: "Internal", label: "Internal" },
                    { value: "External", label: "External" },
                ],
            };
        case "internal_rate":
            return {
                type: "number",
                value: item.internal_rate || 0,
                nonEditable: true,
            };
        case "internal_markup":
            return {
                type: "number",
                value: item.internal_markup || 0,
                nonEditable: true,
            };
        case "vendor_currency":
            return {
                type: "dropdown",
                value: item.vendor_currency || "USD",
                options: CURRENCY_OPTIONS,
                variant: "default",
            };
        case "vendor_rate":
            if (item.is_internal && !isRunning) {
                return {
                    type: "dynamicRate",
                    value: {
                        id: item.service_item_id,
                        vendor_rate: item.vendor_rate,
                        internal_rate: item.internal_rate,
                        currency: item.vendor_currency,
                        rate_type: item.rate_type,
                    },
                } as any;
            } else if (item.is_internal && isRunning) {
                // To keep parity with original functionality, running just shows calculated rate
                return {
                    type: "number",
                    value: item.vendor_rate || 0,
                    nonEditable: true,
                };
            }
            return { type: "number", value: item.vendor_rate || 0 };
        case "rate_type":
            return {
                type: "dropdown",
                value: item.rate_type || "Days",
                options: RATE_TYPE_OPTIONS,
                variant: "chip",
            };
        case "client_rate":
            return {
                type: "number",
                value: item.client_rate || 0,
                nonEditable: true,
            };
        case "budget_buffer":
            return { type: "percentage", value: item.budget_buffer || 0 };
        case "budget_buffer_amount":
            return { type: "number", value: item.budget_buffer_amount || 0 };
        case "client_rate_budget_buffered":
            return {
                type: "number",
                value: item.client_rate_budget_buffered || 0,
                nonEditable: true,
            };
        case "markup":
            return { type: "percentage", value: item.markup || 0 };
        case "markup_amount":
            return { type: "number", value: item.markup_amount || 0 };
        case "client_rate_quoted":
            return {
                type: "number",
                value: item.client_rate_quoted || 0,
                nonEditable: true,
            };
        case "days":
            return { type: "number", value: item.days || 0 };
        case "quantity":
            return { type: "number", value: item.quantity || 0 };
        case "ot_1_5":
            return { type: "number", value: item.ot_1_5 || 0 };
        case "ot_2_0":
            return { type: "number", value: item.ot_2_0 || 0 };
        case "adjustment":
            return { type: "number", value: item.adjustment || 0 };
        case "external_notes":
            return { type: "text", text: item.external_notes || "" };
        case "internal_notes":
            return { type: "text", text: item.internal_notes || "" };
        case "internal_budget":
            return {
                type: "number",
                value: item.internal_budget || 0,
                nonEditable: true,
            };
        case "client_budget":
            return {
                type: "number",
                value: item.client_budget || 0,
                nonEditable: true,
            };
        case "client_quoted":
            return {
                type: "number",
                value: item.client_quoted || 0,
                nonEditable: true,
            };

        // Running specifics:
        case "client_budget_buffered":
            return {
                type: "number",
                value: item.client_budget_buffered || 0,
                nonEditable: true,
            };
        case "variance":
            return {
                type: "number",
                value: item.variance || 0,
                nonEditable: true,
            };
        case "client_budget_buffered_original":
            return {
                type: "number",
                value: item.client_budget_buffered_original || 0,
                nonEditable: true,
            };
        case "client_quoted_original":
            return {
                type: "number",
                value: item.client_quoted_original || 0,
                nonEditable: true,
            };

        default:
            return { type: "text", text: "" };
    }
}
